export enum EnumPickerType {
  SINGLE = 'single',
  MULTIPLE = 'multiple',
  CATEGORY = 'category',
}

export enum EnumAccessibilities {
  ARIA_HIDDEN = 'aria-hidden',
}

const DEFAULT_CONFIG: Omit<IConfig, 'onStateChange'> = {
  picker: {
    type: EnumPickerType.SINGLE, // single, multiple or category (requires categoryAttribute)
    selector: '[data-picker]', // selector for picker container
  },
  buttonSelector: '[data-picker-btn]', // selector for button, switch state on item
  itemSelector: '[data-picker-item]', // selector for item in picker
  activeIndexes: [], // starting item with the active state (array can be empty)
  noSelected: false, // controls whether the last active item can be toggled off
  accessibilities: [EnumAccessibilities.ARIA_HIDDEN], // array of accessibility attributes to be added to the item
  activeClass: 'active', // class name for active item
}

const initializePickers = (config?: Partial<IConfig>) => {
  const currConfig = { ...DEFAULT_CONFIG, ...config } as IConfig // merge default config with passed config

  const pickers = getElements(currConfig.picker.selector)

  if (pickers.length === 0) {
    return console.warn(
      `[PluginPicker] no DOM elements with selector "${currConfig.picker.selector}" found`,
    )
  }

  // initialize pickers by type
  if (currConfig.picker.type === EnumPickerType.SINGLE) {
    handleSingle(pickers, currConfig)
  } else if (currConfig.picker.type === EnumPickerType.MULTIPLE) {
    handleMultiple(pickers, currConfig)
  } else if (currConfig.picker.type === EnumPickerType.CATEGORY) {
    handleCategory(pickers, currConfig)
  } else {
    console.warn(
      `[PluginPicker] invalid picker type - use ${EnumPickerType.SINGLE} or ${EnumPickerType.MULTIPLE}`,
    )
  }
}

// SINGLE PICKER TYPE

const handleSingle = (pickers: NodeListOf<Element>, currConfig: IConfig) => {
  pickers.forEach((picker) => {
    const buttons: NodeListOf<HTMLButtonElement> = picker.querySelectorAll(
      currConfig.buttonSelector,
    )
    const items: NodeListOf<HTMLElement> = picker.querySelectorAll(
      currConfig.itemSelector,
    )

    if (!hasButtons(buttons, currConfig) || !hasItems(items, currConfig)) return

    const localState: ILocalState = {
      activeIndexes: [...currConfig.activeIndexes],
      activeClass: currConfig.activeClass,
      noSelected: currConfig.noSelected,
      accessibilities: currConfig.accessibilities,
    }

    buttons.forEach((button, index) => {
      const elements: ILocalElements = {
        picker,
        buttons: {
          current: button,
          all: buttons,
        },
        items: {
          current: items?.[index],
          all: items,
        },
      }

      setInitialActiveState(elements, localState, index, currConfig)

      // check if button is active and should be skipped if noSelected is false
      const shouldSkipButtonClick = (
        btn: HTMLButtonElement,
        localState: ILocalState,
      ) => {
        return (
          !localState.noSelected &&
          btn.classList.contains(localState.activeClass)
        )
      }

      const handleClick = () => {
        if (shouldSkipButtonClick(elements.buttons.current, localState)) return

        const activeIndex = localState.activeIndexes[0]
        const activeButton = elements.buttons.all?.[activeIndex]

        if (activeButton) {
          activeButton.classList.remove(localState.activeClass)
          toggleAccessibilities(activeButton, localState, true)
        }

        const activeItem = elements.items.all?.[activeIndex]

        if (activeItem) {
          activeItem.classList.remove(localState.activeClass)
          toggleAccessibilities(activeItem, localState, true)
        }

        const isEmptable = activeIndexesCanBeEmptied(localState, index)

        // if array is emptable, reset active indexes and exit before adding active class
        if (isEmptable) {
          localState.activeIndexes = []

          return currConfig.onStateChange?.({
            activeIndexes: localState.activeIndexes,
            type: EnumPickerType.SINGLE,
            element: picker,
          })
        } else {
          localState.activeIndexes = [index]
        }

        elements.buttons.current.classList.add(localState.activeClass)
        elements.items.current.classList.add(localState.activeClass)
        toggleAccessibilities(elements.buttons.current, localState, false)
        toggleAccessibilities(elements.items.current, localState, false)

        currConfig.onStateChange?.({
          activeIndexes: localState.activeIndexes,
          type: EnumPickerType.SINGLE,
          element: picker,
        })
      }

      if (!attachListener(elements)) return

      elements.buttons.current.addEventListener('click', handleClick)
    })
  })
}

// MULTIPLE PICKER TYPE

const handleMultiple = (
  multiplePickers: NodeListOf<Element>,
  currConfig: IConfig,
) => {
  multiplePickers.forEach((picker) => {
    const buttons: NodeListOf<HTMLButtonElement> = picker.querySelectorAll(
      currConfig.buttonSelector,
    )
    const items: NodeListOf<HTMLElement> = picker.querySelectorAll(
      currConfig.itemSelector,
    )

    if (!hasButtons(buttons, currConfig) || !hasItems(items, currConfig)) return

    const localState: ILocalState = {
      activeIndexes: [...currConfig.activeIndexes],
      activeClass: currConfig.activeClass,
      noSelected: currConfig.noSelected,
      accessibilities: currConfig.accessibilities,
    }

    buttons.forEach((button, index) => {
      const elements: ILocalElements = {
        picker,
        buttons: {
          current: button,
          all: buttons,
        },
        items: {
          current: items?.[index],
          all: items,
        },
      }

      setInitialActiveState(elements, localState, index, currConfig)

      // check if button is active and should be skipped if noSelected is false
      const shouldSkipButtonClick = (
        btn: HTMLButtonElement,
        localState: ILocalState,
      ) => {
        return (
          !localState.noSelected &&
          localState.activeIndexes.length === 1 &&
          btn.classList.contains(localState.activeClass)
        )
      }

      const handleClick = () => {
        if (shouldSkipButtonClick(elements.buttons.current, localState)) return

        // find current active index in array of active indexes
        const activeIndex =
          localState.activeIndexes[localState.activeIndexes.indexOf(index)]

        // if button is active, remove active class from button and item
        if (
          elements.buttons.current.classList.contains(localState.activeClass)
        ) {
          const activeButton = elements.buttons.all?.[activeIndex]

          if (activeButton) {
            activeButton.classList.remove(localState.activeClass)
            toggleAccessibilities(activeButton, localState, true)
          }

          const activeItem = elements.items.all?.[activeIndex]

          if (activeItem) {
            activeItem.classList.remove(localState.activeClass)
            toggleAccessibilities(activeItem, localState, true)
          }

          localState.activeIndexes = localState.activeIndexes.filter(
            (i) => i !== index,
          )
        } else {
          // if button is not active, add active class to button and item
          elements.buttons.current.classList.add(localState.activeClass)
          elements.items.current.classList.add(localState.activeClass)
          toggleAccessibilities(elements.buttons.current, localState, false)
          toggleAccessibilities(elements.items.current, localState, false)

          localState.activeIndexes = [...localState.activeIndexes, index]
        }

        currConfig.onStateChange?.({
          activeIndexes: localState.activeIndexes,
          type: EnumPickerType.MULTIPLE,
          element: picker,
        })
      }

      if (!attachListener(elements)) return

      elements.buttons.current.addEventListener('click', handleClick)
    })
  })
}

// CATEGORY PICKER TYPE

const handleCategory = (pickers: NodeListOf<Element>, currConfig: IConfig) => {
  pickers.forEach((picker) => {
    const buttons: NodeListOf<HTMLButtonElement> = picker.querySelectorAll(
      currConfig.buttonSelector,
    )
    const items: NodeListOf<HTMLElement> = picker.querySelectorAll(
      currConfig.itemSelector,
    )

    if (!hasButtons(buttons, currConfig) || !hasItems(items, currConfig)) return

    const localState: ILocalState = {
      activeIndexes: [...currConfig.activeIndexes],
      activeClass: currConfig.activeClass,
      noSelected: currConfig.noSelected,
      accessibilities: currConfig.accessibilities,
    }

    buttons.forEach((button, index) => {
      const elements: ILocalElementsCategory = {
        picker,
        buttons: {
          current: button,
          all: buttons,
        },
        items: {
          current: Array.from(items).filter(
            (item) =>
              isCategoryPicker(currConfig.picker) &&
              Number(item.getAttribute(currConfig.picker.categoryAttribute)) ===
                index,
          ),
          all: items,
        },
      }

      setInitialActiveState(elements, localState, index, currConfig)

      // check if button is active and should be skipped if noSelected is false
      const shouldSkipButtonClick = (
        btn: HTMLButtonElement,
        localState: ILocalState,
      ) => {
        return (
          !localState.noSelected &&
          btn.classList.contains(localState.activeClass)
        )
      }

      const handleClick = () => {
        if (shouldSkipButtonClick(elements.buttons.current, localState)) return

        const activeIndex = localState.activeIndexes[0]
        const activeButton = elements.buttons.all?.[activeIndex]

        if (activeButton) {
          activeButton.classList.remove(localState.activeClass)
          toggleAccessibilities(activeButton, localState, true)
        }

        elements.items.all.forEach((item) => {
          item.classList.remove(localState.activeClass)
          toggleAccessibilities(item, localState, true)
        })

        const isEmptable = activeIndexesCanBeEmptied(localState, index)

        // if array is emptable, reset active indexes and exit before adding active class
        if (isEmptable) {
          localState.activeIndexes = []

          return currConfig.onStateChange?.({
            activeIndexes: localState.activeIndexes,
            type: EnumPickerType.CATEGORY,
            element: picker,
          })
        } else {
          localState.activeIndexes = [index]
        }

        elements.buttons.current.classList.add(localState.activeClass)
        toggleAccessibilities(elements.buttons.current, localState, false)

        elements.items.current.forEach((item) => {
          item.classList.add(localState.activeClass)
          toggleAccessibilities(item, localState, false)
        })

        currConfig.onStateChange?.({
          activeIndexes: localState.activeIndexes,
          type: EnumPickerType.CATEGORY,
          element: picker,
        })
      }

      if (!attachListener(elements)) return

      elements.buttons.current.addEventListener('click', handleClick)
    })
  })
}

// set initial active state, add active class to button and item
// also add accessibilities to button and item
const setInitialActiveState = (
  elements: ILocalElements | ILocalElementsCategory,
  state: ILocalState,
  index: number,
  config: IConfig,
) => {
  // logic for picker type "category"
  if (Array.isArray(elements.items.current)) {
    if (
      !elements.buttons.current.classList.contains(state.activeClass) &&
      state.activeIndexes.includes(index)
    ) {
      elements.buttons.current.classList.add(state.activeClass)
      toggleAccessibilities(elements.buttons.current, state, false)

      elements.items.current.forEach((item) => {
        item.classList.add(state.activeClass)
        toggleAccessibilities(item, state, false)
      })
    }

    elements.buttons.all.forEach((button, idx) => {
      if (idx === state.activeIndexes[0]) return

      toggleAccessibilities(button, state, true)
    })

    elements.items.all.forEach((item) => {
      if (
        isCategoryPicker(config.picker) &&
        Number(item.getAttribute(config.picker.categoryAttribute)) ===
          state.activeIndexes[0]
      )
        return

      toggleAccessibilities(item, state, true)
    })

    return config.onInit?.({
      activeIndexes: state.activeIndexes,
      type: EnumPickerType.CATEGORY,
      element: elements.picker,
    })
  }

  // default logic for non-category type pickers
  if (
    !elements.buttons.current.classList.contains(state.activeClass) &&
    state.activeIndexes.includes(index)
  ) {
    elements.buttons.current.classList.add(state.activeClass)
    elements.items.current?.classList.add(state.activeClass)
    toggleAccessibilities(elements.buttons.current, state, false)
    toggleAccessibilities(elements.items.current, state, false)
  }

  elements.buttons.all.forEach((button, idx) => {
    if (state.activeIndexes.some((i) => idx === i)) return

    toggleAccessibilities(button, state, true)
  })

  elements.items.all.forEach((item, idx) => {
    if (state.activeIndexes.some((i) => idx === i)) return

    toggleAccessibilities(item, state, true)
  })

  config.onInit?.({
    activeIndexes: state.activeIndexes,
    type: EnumPickerType.CATEGORY,
    element: elements.picker,
  })
}

const activeIndexesCanBeEmptied = (localState: ILocalState, index: number) => {
  return localState.noSelected && localState.activeIndexes.includes(index)
}

// handle accessibilities for buttons and items e.g toggling "aria-hidden" attribute alongside active class
const toggleAccessibilities = (
  element: HTMLElement | HTMLButtonElement,
  state: ILocalState,
  condition: boolean,
) => {
  const accessibilities = state.accessibilities

  if (accessibilities.length === 0) return

  accessibilities.forEach((accessibility) => {
    element?.setAttribute(accessibility, condition ? 'true' : 'false')
  })
}

function isCategoryPicker(picker: TPickerTypes): picker is TPickerCategory {
  return picker.type === EnumPickerType.CATEGORY
}

function attachListener(elements: ILocalElements | ILocalElementsCategory) {
  if (elements.buttons.current.dataset.pickerListenerAttached === 'true') {
    return false
  }

  elements.buttons.current.dataset.pickerListenerAttached = 'true'

  return true
}

const getElements = (selector?: string) => {
  if (selector) {
    return document.querySelectorAll(selector)
  } else {
    return [] as unknown as NodeListOf<Element>
  }
}

const hasButtons = (buttons: NodeListOf<Element>, currConfig: IConfig) => {
  if (buttons.length === 0) {
    console.warn(
      `[PluginPicker] no DOM buttons with selector "${currConfig.buttonSelector}" found`,
    )

    return false
  }

  return true
}

const hasItems = (items: NodeListOf<Element>, currConfig: IConfig) => {
  if (items.length === 0) {
    console.warn(
      `[PluginPicker] no DOM items with selector "${currConfig.itemSelector}" found`,
    )

    return false
  }

  return true
}

export default initializePickers

// TYPES

type TPickerCategory = {
  type: EnumPickerType.CATEGORY
  selector: string
  categoryAttribute: string
}

type TPickerRest = {
  type: EnumPickerType.SINGLE | EnumPickerType.MULTIPLE
  selector: string
}

type TPickerTypes = TPickerCategory | TPickerRest

type TCallbackState = (state: {
  activeIndexes: number[]
  type: EnumPickerType
  element: Element
}) => void

interface IConfig {
  picker: TPickerTypes
  buttonSelector: string
  itemSelector: string
  activeIndexes: number[]
  noSelected: boolean
  activeClass: string
  accessibilities: EnumAccessibilities[]
  onStateChange?: TCallbackState
  onInit?: TCallbackState
}

interface ILocalState {
  activeIndexes: number[]
  activeClass: string
  noSelected: boolean
  accessibilities: EnumAccessibilities[]
}

interface ILocalElements {
  picker: Element
  buttons: {
    current: HTMLButtonElement
    all: NodeListOf<HTMLButtonElement>
  }
  items: {
    current: HTMLElement
    all: NodeListOf<HTMLElement>
  }
}

interface ILocalElementsCategory {
  picker: Element
  buttons: {
    current: HTMLButtonElement
    all: NodeListOf<HTMLButtonElement>
  }
  items: {
    current: HTMLElement[]
    all: NodeListOf<HTMLElement>
  }
}
